import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { LogIn, MoreHorizontal, Send, EyeOff, Crown, ArrowLeftRight, Power, Gift } from 'lucide-react';
import { formatCurrency } from '../../../lib/utils';
import { PRIMARY_COLOR, colorVars } from './groupColors';

const money = (v) => `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Scheda utente della griglia. Solo aspetto: tutte le azioni arrivano dalle props.
export const UserCard = ({
  user,
  accent = PRIMARY_COLOR,
  isAdmin = false,
  isAdminPrimary = false,
  onOpen,
  onEnter,
  onNominate,
  onTransfer,
  onToggleActive,
  activeFreePeriod = null,
  formatDateDisplay = (d) => d,
}) => {
  const ready = user.ready_accounts || 0;
  const state = ready === 0 ? 'alert' : ready === 1 ? 'warn' : 'ok';
  const slots = Math.min(4, Math.max(0, user.active_slots_count || 0));
  const compenso = user.compenso_da_pagare_usd || 0;
  const profit = user.total_profits || 0;
  const month = user.profit_this_month || 0;
  const neonClass = state === 'ok' ? 'neon n2' : state === 'warn' ? 'neon n4 cy' : 'neon n4 cr';
  const ringColor = state === 'warn' ? 'var(--ht-amber)' : state === 'alert' ? 'var(--ht-red)' : accent.c;

  return (
    <div
      onClick={() => onOpen?.(user)}
      style={state === 'ok' ? { ...colorVars(accent), '--ht-ring-c': ringColor } : { '--ht-gc': accent.c, '--ht-ring-c': ringColor }}
      className={`ht-panel ht-state-card ${state === 'warn' ? 'ht-state-warn' : state === 'alert' ? 'ht-state-alert' : ''} ${neonClass} relative flex flex-col h-full cursor-pointer`}
      data-testid={`user-card-${user.id}`}
    >
      <div className="p-3 pl-4 flex flex-col gap-2.5 flex-1">
        {/* richieste in sospeso (contenuto esistente) */}
        {user.pending_requests && user.pending_requests.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {user.pending_requests.map((req) => (
              <span key={req.id} className={`ht-tag ${req.request_type === 'renewal' ? '' : 'ht-tag-warn'}`}>
                {req.request_type === 'renewal' ? 'Rinnovo' : 'Sospensione'}
              </span>
            ))}
          </div>
        )}

        {/* intestazione */}
        <div className="flex items-center gap-2">
          <Avatar className="h-9 w-9 flex-shrink-0 ht-ring">
            {user.profile_photo ? <AvatarImage src={user.profile_photo} alt={user.name} className="object-cover" /> : null}
            <AvatarFallback className="bg-[#1a2026] text-white text-xs font-medium">
              {user.name?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <h3 className="font-heading font-semibold text-white text-sm leading-snug break-words line-clamp-2 min-w-0 flex-1" title={user.name}>
            {user.name}
          </h3>
          <button
            onClick={(e) => { e.stopPropagation(); onEnter?.(user); }}
            className="btn-magnetic btn-entra"
            title="Entra"
            aria-label={`Entra nel profilo di ${user.name}`}
            data-magnetic
            data-magnetic-x="4"
            data-magnetic-y="4"
          >
            <LogIn className="w-[18px] h-[18px] sm:w-4 sm:h-4 shrink-0" />
            <span className="btn-entra-label">Entra</span>
          </button>
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="ht-btn ht-btn-icon flex-shrink-0"
                  title="Altre azioni"
                  data-testid={`user-card-menu-${user.id}`}
                >
                  <MoreHorizontal size={15} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0d1117] border-[#282c34] min-w-[190px]">
                {isAdminPrimary && user.role !== 'admin_secondary' && (
                  <>
                    <DropdownMenuItem
                      className="ht-menu-item"
                      onClick={(e) => { e.stopPropagation(); onNominate?.(user); }}
                      data-testid={`nominate-admin-${user.id}`}
                    >
                      <Crown size={13} /> Nomina Admin
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="ht-menu-item"
                      onClick={(e) => { e.stopPropagation(); onTransfer?.(user); }}
                      data-testid={`transfer-user-${user.id}`}
                    >
                      <ArrowLeftRight size={13} /> Trasferisci
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#282c34]" />
                  </>
                )}
                <DropdownMenuItem
                  className="ht-menu-item ht-menu-item-danger"
                  onClick={(e) => { e.stopPropagation(); onToggleActive?.(user); }}
                  data-testid={`toggle-active-${user.id}`}
                >
                  <Power size={13} /> {user.is_active !== false ? 'Disattiva' : 'Attiva'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* etichette */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="ht-tag">{user.management_type === 'managed' ? 'Gestito' : 'Autonomo'}</span>
          {user.view_only && (
            <span className="ht-tag"><EyeOff size={10} /> View Only</span>
          )}
          {!user.is_approved && <span className="ht-tag ht-tag-warn">In attesa</span>}
          {user.telegram_chat_id && (
            <Send size={12} className="text-[#5f6672]" title="Telegram collegato" data-testid={`telegram-linked-${user.id}`} />
          )}
        </div>

        {/* righe sottili */}
        <div className="ht-rows">
          <div className="ht-row">
            <span className="ht-row-label">Profitto totale</span>
            <span className={`ht-row-value ${profit > 0 ? 'ht-pos' : ''}`}>{formatCurrency(profit)}</span>
          </div>
          <div className="ht-row">
            <span className="ht-row-label">Mese corrente</span>
            <span className="ht-row-value flex items-center gap-2 justify-end">
              {user.stats?.monthly_capital > 0 && (
                <span className={`text-[10px] ${(user.stats?.monthly_percent || 0) >= 0 ? 'ht-pos' : 'ht-alert'}`}>
                  {(user.stats?.monthly_percent || 0) >= 0 ? '+' : ''}{(user.stats?.monthly_percent || 0).toFixed(1)}%
                </span>
              )}
              <span className={month > 0 ? 'ht-pos' : month < 0 ? 'ht-alert' : ''}>{formatCurrency(month)}</span>
            </span>
          </div>
          <div className="ht-row">
            <span className="ht-row-label">Slot attivi</span>
            <span className="ht-row-value flex items-center gap-2 justify-end">
              <span className="ht-ticks">
                {[0, 1, 2, 3].map((i) => <span key={i} className={`ht-tick ${i < slots ? 'ht-tick-on' : ''}`} />)}
              </span>
              <span>{slots}/4</span>
            </span>
          </div>
          <div className="ht-row">
            <span className="ht-row-label">Conti pronti</span>
            <span className="ht-row-value flex items-center gap-2 justify-end">
              <span className={`ht-dot ${state === 'warn' ? 'ht-dot-warn' : state === 'alert' ? 'ht-dot-alert' : ''}`} />
              <span className={state === 'warn' ? 'ht-warn' : state === 'alert' ? 'ht-alert' : ''}>{ready}</span>
            </span>
          </div>
          <div className="ht-row">
            <span className="ht-row-label">Wallet</span>
            <span className="ht-row-value">{money((user.wallet_balance_usdt || 0) + (user.wallet_balance_usdc || 0))}</span>
          </div>
          <div className="ht-row">
            <span className="ht-row-label">Broker</span>
            <span className="ht-row-value">{money(user.broker_balance)}</span>
          </div>
          <div className="ht-row">
            <span className="ht-row-label">Compenso da pagare</span>
            <span className={`ht-row-value ${compenso > 0 ? 'ht-warn' : 'ht-off'}`}>
              {compenso > 0 ? `$${compenso.toFixed(2)}` : '$0'}
            </span>
          </div>
        </div>

        {activeFreePeriod && (
          <div className="flex items-center gap-2">
            <span className="ht-tag"><Gift size={10} /> Gratis fino al {formatDateDisplay(activeFreePeriod.end_date)}</span>
          </div>
        )}
      </div>

      {/* fascia capitale ancorata in basso */}
      <div className="ht-strip">
        <span className="ht-row-label">Capitale totale</span>
        <span className="ht-row-value text-white">{money(user.capitale_totale)}</span>
      </div>
    </div>
  );
};

export default UserCard;
